import { Link } from "react-router-dom";
import { Settings, Trash2, AlertTriangle, Smartphone, Mail } from "lucide-react";
import SEO from "@/components/SEO";
import { PageHeader } from "@/components/PagePrimitives";
import Reveal from "@/components/Reveal";
import { SITE } from "@/config/site";

const STEPS = [
  "Open Nova and sign in to your account.",
  "Go to Settings.",
  "Choose the option to delete your account.",
  "Confirm the deletion when prompted.",
];

const DELETED = [
  "Your name, email and user ID",
  "Your financial information: income, expenses, debts, savings and goals",
  "Uploaded bank statements and analysis results",
  "Retained AI prompts and responses, support history and product interaction data",
];

export default function DeleteAccount() {
  return (
    <>
      <SEO title="Delete Your Nova Account" description="How to delete your Nova account and data from inside the app, what is removed, and how to cancel an active subscription." path="/delete-account" />
      <PageHeader eyebrow="Your account" title="Delete your account" subtitle="You can delete your Nova account and data directly inside the app. Here's exactly how, and what happens to your information and any active subscription." />

      <section className="pb-24">
        <div className="container-nova max-w-3xl space-y-8">
          <Reveal>
            <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6">
              <div className="mb-4 flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-nova-cyan"><Settings size={18} /></div>
                <h2 className="font-heading text-xl font-bold text-white">Delete from inside Nova</h2>
              </div>
              <ol className="space-y-3">
                {STEPS.map((s, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-white/70" data-testid={`delete-step-${i + 1}`}>
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-white/15 bg-nova-surface text-xs font-bold text-nova-cyan">{i + 1}</span>
                    {s}
                  </li>
                ))}
              </ol>
            </div>
          </Reveal>

          <Reveal>
            <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6">
              <div className="mb-4 flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-nova-emerald"><Trash2 size={18} /></div>
                <h2 className="font-heading text-xl font-bold text-white">What gets deleted</h2>
              </div>
              <ul className="space-y-2.5">
                {DELETED.map((d) => (
                  <li key={d} className="flex items-start gap-2.5 text-sm text-white/65">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-nova-emerald" /> {d}
                  </li>
                ))}
              </ul>
              <p className="mt-4 text-sm leading-relaxed text-white/55">
                Deletion removes your personal data from your account. Depending on the request, some removals may be processed immediately while others may be queued and completed shortly afterwards, including the removal of associated backups. We may retain limited records only where required to meet legal, tax or accounting obligations, as described in our <Link to="/privacy" className="text-nova-cyan hover:underline">Privacy Policy</Link>. Nova is owned and operated by {SITE.legalOwner}.
              </p>
            </div>
          </Reveal>

          <Reveal>
            <div className="rounded-2xl border border-yellow-400/25 bg-yellow-400/[0.05] p-6" data-testid="delete-subscription-warning">
              <div className="mb-4 flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-yellow-400/30 bg-yellow-400/10 text-yellow-300"><AlertTriangle size={18} /></div>
                <h2 className="font-heading text-xl font-bold text-white">Important: your subscription</h2>
              </div>
              <p className="text-sm leading-relaxed text-white/70">
                Deleting your Nova account does <span className="font-semibold text-white">not</span> automatically cancel an active Nova Premium subscription. Subscriptions are purchased through Apple In-App Purchase, so you must cancel separately through your Apple subscription settings if you no longer want it to renew.
              </p>
              <div className="mt-4 rounded-xl border border-white/10 bg-white/[0.03] p-4">
                <p className="flex items-center gap-2 text-sm font-semibold text-white"><Smartphone size={15} /> Cancel through Apple</p>
                <p className="mt-2 text-sm text-white/60">Open Settings on your device → tap your name → Subscriptions → select Nova → Cancel Subscription. Cancellation normally takes effect at the end of the current billing period.</p>
              </div>
            </div>
          </Reveal>

          <Reveal>
            <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6">
              <div className="mb-3 flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-nova-cyan"><Mail size={18} /></div>
                <h2 className="font-heading text-xl font-bold text-white">Need help deleting?</h2>
              </div>
              <p className="text-sm text-white/60">
                If you have trouble deleting your account, email <a href={`mailto:${SITE.supportEmail}`} className="text-nova-cyan hover:underline" data-testid="delete-support-email">{SITE.supportEmail}</a> or visit our <Link to="/support" className="text-nova-cyan hover:underline">Support Centre</Link>, and we'll help.
              </p>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
